import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";

import CustomTableService from "services/CustomTable";
import {
  customTableTargetSlug,
  customTableTargetType,
  isCustomTableTarget,
  STANDARD_TARGET_OPTIONS,
} from "routes/custom_tables/relationshipRoutes";

// Any scalar column can label a row — mirrors the backend's
// LABEL_ELIGIBLE_TYPES (multi-valued and relationship columns excluded).
// Shared with the SchemaEditor "Display column" picker.
export const LABEL_FIELD_TYPES = new Set([
  "short_text",
  "text",
  "number",
  "date",
  "boolean",
  "single_select",
  "email",
  "url",
  "phone",
  "percentage",
  "currency",
]);

/**
 * Target options for relationship columns: the standard models plus every
 * custom table. Pass `enabled=false` until a relationship field is in play
 * so non-relationship usage never pays for the list call.
 */
export function useRelationshipTargets(enabled) {
  const { data: customTablesPage } = useQuery({
    queryKey: ["custom-tables-relationship-targets"],
    queryFn: () => CustomTableService.list(),
    enabled,
  });

  const targetOptions = useMemo(() => {
    const tables = customTablesPage?.data ?? [];
    return [
      ...STANDARD_TARGET_OPTIONS,
      ...tables.map(t => ({
        value: customTableTargetType(t.slug),
        label: `${t.display_name} (custom table)`,
      })),
    ];
  }, [customTablesPage]);

  // Label-column override only applies to custom-table targets; candidates
  // are the target's scalar columns (the backend validates the same set).
  const labelFieldOptionsFor = useCallback(
    targetType => {
      if (!isCustomTableTarget(targetType)) return [];
      const slug = customTableTargetSlug(targetType);
      const table = (customTablesPage?.data ?? []).find(t => t.slug === slug);
      return (table?.columns ?? [])
        .filter(c => LABEL_FIELD_TYPES.has(c.type))
        .map(c => ({ value: c.field_id, label: c.display_name }));
    },
    [customTablesPage]
  );

  return { targetOptions, labelFieldOptionsFor };
}
